import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { BE_API_AUTH_URL } from '../../constants/app.constants';
import { AdminSignOrderService, AdminOrderDetail, AdminOrderItem } from '../../services/admin-sign-order.service';
import { SignOrderPdfService, SignOrderPdfError } from '../../services/sign-order-pdf.service';
import { formatSignOrderDateTime } from '../../sign-orders/sign-order-date.utils';
import { formatSignOrderWeight } from '../../sign-orders/sign-order-weight.utils';

@Component({
  selector: 'app-admin-sign-order-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule, FormsModule,
    NzAlertComponent, NzButtonModule, NzCardModule, NzDividerModule,
    NzIconModule, NzInputModule, NzSpinModule, NzTableModule, NzTagModule,
    NzTimelineModule, NzToolTipModule, NzModalModule,
    TranslateModule,
  ],
  templateUrl: './sign-order-detail.component.html',
  styleUrls: ['./sign-order-detail.component.less'],
})
export class AdminSignOrderDetailComponent implements OnInit, OnDestroy {
  readonly formatSignOrderDateTime = formatSignOrderDateTime;
  readonly formatSignOrderWeight = formatSignOrderWeight;

  order: AdminOrderDetail | null = null;
  orderItems: any[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  isGeneratingPdf = false;
  isGeneratingCombinedPdf = false;
  generatingItemId: string | null = null;
  pdfErrorMessage: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private adminService: AdminSignOrderService,
    private pdfService: SignOrderPdfService,
    private http: HttpClient,
    private modal: NzModalService,
    private message: NzMessageService,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    const publicId = this.route.snapshot.paramMap.get('publicId');
    if (publicId) {
      this.loadOrder(publicId);
    } else {
      this.isLoading = false;
      this.errorMessage = this.translate.instant('SIGN_ORDER.ADMIN.INVALID_ID');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrder(publicId: string): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.adminService.getOrderDetail(publicId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (order) => {
        this.order = order;
        this.orderItems = [...(order.items || [])];
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.status === 404
          ? this.translate.instant('SIGN_ORDER.ADMIN.NOT_FOUND')
          : this.translate.instant('SIGN_ORDER.ADMIN.ERROR_LOADING');
      },
    });
  }

  formatCents(cents: number, currency: string): string {
    const lang = this.translate.currentLang === 'de' ? 'de-DE' : 'en-US';
    const amount = (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${amount} ${currency}`;
  }

  localizedText(text: { de: string; en: string; [key: string]: string } | undefined): string {
    if (!text) return '';
    const lang = this.translate.currentLang || 'de';
    return text[lang] || text.de || text.en || '';
  }

  getStatusLabel(status: string): string {
    return this.translate.instant(`SIGN_ORDER.STATUS.${status.toUpperCase()}`);
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      pending_payment: 'orange', payment_received: 'blue', processing: 'processing',
      ordered_from_supplier: 'cyan', received_from_supplier: 'geekblue',
      shipped: 'purple', completed: 'green', cancelled: 'red',
    };
    return colors[status] || 'default';
  }

  formatVatRate(basisPoints: number): string {
    return `${(basisPoints / 100).toFixed(1)}%`;
  }

  formatTierRange(tier: { minQuantity: number; maxQuantity: number | null } | null | undefined): string {
    if (!tier) return '';
    return tier.maxQuantity === null ? `${tier.minQuantity}+` : `${tier.minQuantity}–${tier.maxQuantity}`;
  }

  async previewItem(item: AdminOrderItem): Promise<void> {
    if (!this.order) return;
    if (this.isGeneratingPdf) return;
    this.clearPdfError();
    this.isGeneratingPdf = true;
    this.generatingItemId = item.propertyId;
    try {
      await this.pdfService.previewItem(this.order, item);
    } catch (err: any) {
      this.handlePdfError(err, item);
    } finally {
      this.isGeneratingPdf = false;
      this.generatingItemId = null;
    }
  }

  async downloadIndividualPdf(item: AdminOrderItem): Promise<void> {
    if (!this.order) return;
    if (this.isGeneratingPdf) return;
    this.clearPdfError();
    this.isGeneratingPdf = true;
    this.generatingItemId = item.propertyId;
    try {
      await this.pdfService.downloadIndividualPdf(this.order, item);
    } catch (err: any) {
      this.handlePdfError(err, item);
    } finally {
      this.isGeneratingPdf = false;
      this.generatingItemId = null;
    }
  }

  async downloadCombinedPdf(): Promise<void> {
    if (this.isGeneratingCombinedPdf || !this.order) return;
    this.clearPdfError();
    this.isGeneratingCombinedPdf = true;
    try {
      await this.pdfService.downloadCombinedPdf(this.order);
    } catch (err: any) {
      const msg = err.message || this.translate.instant('SIGN_ORDER.ADMIN.PDF_GENERATION_ERROR');
      this.pdfErrorMessage = msg;
      this.message.error(msg);
    } finally {
      this.isGeneratingCombinedPdf = false;
    }
  }

  isItemGenerating(item: AdminOrderItem): boolean {
    return this.generatingItemId === item.propertyId;
  }

  private handlePdfError(err: any, item: AdminOrderItem): void {
    const propId = item.identifier || item.address || item.propertyId;
    const msg = err instanceof SignOrderPdfError
      ? err.message
      : `${this.translate.instant('SIGN_ORDER.ADMIN.PDF_GENERATION_ERROR')} (${propId})`;
    this.pdfErrorMessage = msg;
    this.message.error(msg);
  }

  private clearPdfError(): void {
    this.pdfErrorMessage = null;
  }

  apiPath(path: string): string {
    return `${BE_API_AUTH_URL}/admin/sign-orders/${path}`;
  }

  confirmPayment(): void {
    if (!this.order) return;
    if (!this.hasInvoiceNumber) {
      this.message.warning(this.translate.instant('SIGN_ORDER.ADMIN.INVOICE_NUMBER_REQUIRED_BEFORE_PAYMENT'));
      return;
    }
    this.modal.confirm({
      nzTitle: this.translate.instant('SIGN_ORDER.ADMIN.CONFIRM_PAYMENT_TITLE'),
      nzOnOk: () => new Promise((resolve) => {
        this.http.post(this.apiPath(`${this.order!.publicId}/confirm-payment`), {}).subscribe({
          next: () => { this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.ACTION_SUCCESS')); this.loadOrder(this.order!.publicId); resolve(); },
          error: (err) => { this.message.error(err.error?.message || ''); resolve(); },
        });
      }),
    });
  }

  doTransition(nextStatus: string, extra: any = {}): void {
    if (!this.order) return;
    this.http.post(this.apiPath(`${this.order.publicId}/transition`), { nextStatus, ...extra }).subscribe({
      next: () => { this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.ACTION_SUCCESS')); this.loadOrder(this.order!.publicId); },
      error: (err) => this.message.error(err.error?.message || ''),
    });
  }

  transitionTo(nextStatus: string): void {
    if (!this.order) return;
    if (this.isSubmittingAction) return;

    if (nextStatus === 'payment_received') {
      this.confirmPayment();
      return;
    }

    if (nextStatus === 'cancelled') {
      this.cancelReason = '';
      this.cancelModalVisible = true;
      return;
    }

    if (nextStatus === 'shipped') {
      this.shipCarrier = '';
      this.shipTracking = '';
      this.shipUrl = '';
      this.shipNote = '';
      this.shipmentModalVisible = true;
      return;
    }

    this.doAction(this.apiPath(`${this.order!.publicId}/transition`), { nextStatus });
  }

  submitCancellation(): void {
    const reason = (this.cancelReason || '').trim();
    if (!reason) {
      this.message.warning(this.translate.instant('SIGN_ORDER.ADMIN.CANCEL_REASON_REQUIRED'));
      return;
    }
    this.doAction(this.apiPath(`${this.order!.publicId}/transition`), { nextStatus: 'cancelled', reason });
  }

  submitShipment(): void {
    this.doAction(this.apiPath(`${this.order!.publicId}/transition`), {
      nextStatus: 'shipped',
      shipment: { carrier: this.shipCarrier, trackingNumber: this.shipTracking, trackingUrl: this.shipUrl, note: this.shipNote },
    });
  }

  private doAction(url: string, body: any): Promise<void> {
    return new Promise((resolve) => {
      this.isSubmittingAction = true;
      this.http.post(url, body).subscribe({
        next: () => {
          this.isSubmittingAction = false;
          this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.ACTION_SUCCESS'));
          this.loadOrder(this.order!.publicId);
          resolve();
        },
        error: (err) => {
          this.isSubmittingAction = false;
          this.message.error(err.error?.message || '');
          resolve();
        },
      });
    });
  }

  isSubmittingAction = false;
  isRetryingEmail = false;
  cancelModalVisible = false;
  cancelReason = '';
  shipmentModalVisible = false;
  shipCarrier = '';
  shipTracking = '';
  shipUrl = '';
  shipNote = '';
  invoiceNumberModalVisible = false;
  invoiceNumberDraft = '';
  invoiceNumberValidationError = '';
  isSavingInvoiceNumber = false;

  get hasInvoiceNumber(): boolean {
    return !!this.order?.invoiceNumber?.trim();
  }

  openInvoiceNumberModal(): void {
    if (!this.order || this.order.status !== 'pending_payment') return;
    this.invoiceNumberDraft = this.order.invoiceNumber || '';
    this.invoiceNumberValidationError = '';
    this.invoiceNumberModalVisible = true;
  }

  closeInvoiceNumberModal(): void {
    if (this.isSavingInvoiceNumber) return;
    this.invoiceNumberModalVisible = false;
    this.invoiceNumberValidationError = '';
    this.invoiceNumberDraft = this.order?.invoiceNumber || '';
  }

  submitInvoiceNumber(): void {
    if (!this.order || this.order.status !== 'pending_payment' || this.isSavingInvoiceNumber) return;
    const invoiceNumber = this.invoiceNumberDraft.trim();
    if (!invoiceNumber) {
      this.invoiceNumberValidationError = this.translate.instant('SIGN_ORDER.ADMIN.INVOICE_NUMBER_REQUIRED');
      return;
    }

    this.invoiceNumberValidationError = '';
    this.isSavingInvoiceNumber = true;
    this.http.put(this.apiPath(`${this.order.publicId}/invoice-number`), { invoiceNumber }).subscribe({
      next: () => {
        this.isSavingInvoiceNumber = false;
        this.closeInvoiceNumberModal();
        this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.ACTION_SUCCESS'));
        this.loadOrder(this.order!.publicId);
      },
      error: (err) => {
        this.isSavingInvoiceNumber = false;
        this.invoiceNumberValidationError = err.error?.message || this.translate.instant('SIGN_ORDER.ADMIN.INVOICE_NUMBER_INVALID');
      },
    });
  }

  saveNotes(): void {
    if (!this.order) return;
    this.http.put(this.apiPath(`${this.order.publicId}/notes`), {
      internalNotes: this.order.internalNotes,
      userVisibleNote: this.order.userVisibleNote,
    }).subscribe({
      next: () => this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.ACTION_SUCCESS')),
      error: (err) => this.message.error(err.error?.message || ''),
    });
  }

  getCombinedPdfPages(): number {
    if (!this.order?.items) return 0;
    return this.order.items.reduce((sum, i) => sum + i.copies, 0);
  }

  retryEmail(eventKey: string): void {
    if (this.isRetryingEmail || !this.order) return;
    this.isRetryingEmail = true;
    this.adminService.retryEmailEvent(this.order.publicId, eventKey).subscribe({
      next: () => {
        this.isRetryingEmail = false;
        this.message.success(this.translate.instant('SIGN_ORDER.ADMIN.ACTION_SUCCESS'));
        this.loadOrder(this.order!.publicId);
      },
      error: (err) => {
        this.isRetryingEmail = false;
        this.message.error(err.error?.message || '');
      },
    });
  }

  get allowedTransitions(): string[] {
    if (!this.order) return [];
    const map: Record<string, string[]> = {
      pending_payment: ['payment_received', 'cancelled'],
      payment_received: ['processing', 'cancelled'],
      processing: ['ordered_from_supplier', 'cancelled'],
      ordered_from_supplier: ['received_from_supplier', 'cancelled'],
      received_from_supplier: ['shipped', 'cancelled'],
      shipped: ['completed'],
      completed: [],
      cancelled: [],
    };
    return map[this.order.status] || [];
  }
}
